/* Auth/session state. Wraps the typed Matrix client; surfaces login errors
   instead of swallowing them. No demo/guest fallback — real auth only. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { MatrixError } from '@/types/matrix'

interface SessionState {
  loggedIn: boolean
  userId: string | null
  displayName: string | null
  /** mxc:// URI of the account avatar, when one is set. */
  avatarUrl: string | null
  homeserverHost: string | null
  // Why we were signed out involuntarily (token died) — surfaced on the login
  // screen, then cleared. Null after a clean manual sign-out.
  expiredReason: string | null
}

function prettyError(err: unknown): string {
  if (err instanceof MatrixError) {
    switch (err.errcode) {
      case 'M_FORBIDDEN':
      case 'M_UNAUTHORIZED':
        return 'Wrong username or password.'
      case 'M_USER_DEACTIVATED':
        return 'This account has been deactivated.'
      case 'M_LIMIT_EXCEEDED':
        return 'Too many attempts — wait a moment and try again.'
      case 'M_NO_HOMESERVER':
      case 'M_NETWORK':
        return 'Could not reach the homeserver.'
    }
    return err.message || err.errcode || 'Sign-in failed.'
  }
  return 'Sign-in failed. Is the homeserver reachable?'
}

export const useSession = defineStore('session', {
  state: (): SessionState => ({
    loggedIn: matrix.hasSession(),
    userId: matrix.userId() ?? null,
    displayName: null,
    avatarUrl: null,
    homeserverHost: null,
    expiredReason: null,
  }),
  actions: {
    async discoverHost() {
      const d = await matrix.discover()
      try {
        this.homeserverHost = new URL(d.baseUrl).host
      } catch {
        this.homeserverHost = d.baseUrl || null
      }
    },
    async loginPassword(user: string, password: string): Promise<void> {
      const s = await matrix.loginPassword(user, password)
      this.userId = s.userId
      this.loggedIn = true
      void this.loadProfile()
    },
    async completeSSO(loginToken: string): Promise<void> {
      const s = await matrix.completeSSO(loginToken)
      this.userId = s.userId
      this.loggedIn = true
      void this.loadProfile()
    },
    async ssoRedirect() {
      await matrix.ssoRedirect()
    },
    async loadProfile() {
      const id = matrix.userId()
      if (!id) return
      const p = await matrix.profile(id)
      this.displayName = p.displayname ?? null
      this.avatarUrl = p.avatar_url || null
    },
    async logout() {
      await matrix.logout()
      this.reset(null)
    },
    /** Sign out without touching the server — for when the token is already
     *  dead. `reason` is shown on the login screen so the user knows why. The
     *  loggedIn → false flip is watched in App.vue, which routes to /login. */
    forceLogout(reason: string) {
      matrix.clearSession()
      this.reset(reason)
    },
    reset(reason: string | null) {
      this.loggedIn = false
      this.userId = null
      this.displayName = null
      this.avatarUrl = null
      this.expiredReason = reason
    },
    takeExpiredReason(): string | null {
      const r = this.expiredReason
      this.expiredReason = null
      return r
    },
    errorMessage(err: unknown): string {
      return prettyError(err)
    },
  },
})
